// 🔐 accounts (09-30): Google's own "Continue with Google" button (Google
// Identity Services). The script loads only when a sign-in card is on screen,
// never on page load. Google's popup hands back an ID token, which goes
// straight to our backend (accountState.signInWithGoogle) to be checked.
import React, { useEffect, useRef, useState } from 'react';

const GIS_SRC = 'https://accounts.google.com/gsi/client';
let gisPromise = null;
let initializedFor = null;
let currentHandler = null; // the card on screen right now

function loadGis() {
  if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve(window.google);
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GIS_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => { gisPromise = null; reject(new Error('Google sign-in did not load')); };
    document.head.appendChild(s);
  });
  return gisPromise;
}

export default function GoogleButton({ clientId, dark = true, onCredential, width = 296 }) {
  const boxRef = useRef(null);
  const handlerRef = useRef(onCredential);
  handlerRef.current = onCredential;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    currentHandler = (credential) => { if (handlerRef.current) handlerRef.current(credential); };
    if (!clientId) return undefined;
    loadGis().then((g) => {
      if (!alive || !boxRef.current || !g || !g.accounts || !g.accounts.id) return;
      if (initializedFor !== clientId) {
        g.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => { if (currentHandler) currentHandler(resp && resp.credential); },
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true,
        });
        initializedFor = clientId;
      }
      boxRef.current.replaceChildren();
      g.accounts.id.renderButton(boxRef.current, {
        type: 'standard', theme: dark ? 'filled_black' : 'outline', size: 'large',
        shape: 'pill', text: 'continue_with', logo_alignment: 'left', width,
      });
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [clientId, dark, width]);

  if (failed) return <p className="ms-acct-error">Google sign-in didn't load. Check your connection and try again.</p>;
  return <div ref={boxRef} className="ms-acct-gbtn" />;
}
